// Unified public-API wrapper used by ALL non-authenticated frontend
// callsites (consent, campaign reveal, public branding, signup, etc).
//
// WHY THIS EXISTS:
//   • Raw `fetch(...).then(r => r.json())` is brittle: in some browsers
//     (Chrome/iOS Safari with active service worker) the response body
//     stream can end up "consumed" by another consumer before our
//     `.json()` / `.text()` call → throws "Failed to execute 'text' on
//     'Response': body stream already read" / "Body is disturbed or
//     locked". We hit this in production with the campaign reveal flow.
//   • Axios uses XMLHttpRequest under the hood (we force the `xhr`
//     adapter explicitly, see below) — XHR has no concept of a body
//     stream, so it's structurally immune to that whole class of bugs.
//   • Centralising here means one place to add interceptors (retry,
//     telemetry, offline queueing) for all public flows.
//
// USAGE:
//   const { data, status } = await apiGet('/api/public/campaigns/abc');
//   const { data, status } = await apiPost('/api/public/consent/xyz', { marketing: true });

import axios from 'axios';
import { getBackendUrl } from './origin';

// Note on adapter: axios v1.x ships with both `xhr` (XMLHttpRequest)
// and `fetch` adapters. In browsers it defaults to `xhr` — which is
// what we want, since XHR has no concept of a streaming response body
// and therefore cannot be "locked" or "disturbed" by a concurrent
// consumer (notably a service worker on iOS Safari). We rely on this
// default rather than forcing an adapter that may move modules between
// minor versions; the structural immunity is a property of XHR itself.

function fullUrl(path) {
  if (!path) return getBackendUrl();
  if (/^https?:\/\//i.test(path)) return path;
  return `${getBackendUrl()}${path.startsWith('/') ? '' : '/'}${path}`;
}

const baseConfig = {
  // Treat 4xx/5xx as successful resolutions so the caller can branch on
  // status without a try/catch dance — mirrors how PHP/Java teams expect
  // HTTP clients to behave for "expected" failure cases (404 not found,
  // 401 invalid PIN, 400 validation).
  validateStatus: () => true,
};

export async function apiGet(path, opts = {}) {
  return axios.get(fullUrl(path), { ...baseConfig, ...opts });
}

export async function apiPost(path, body = undefined, opts = {}) {
  return axios.post(fullUrl(path), body, {
    ...baseConfig,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
}

export async function apiPut(path, body = undefined, opts = {}) {
  return axios.put(fullUrl(path), body, {
    ...baseConfig,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
}

export async function apiDelete(path, opts = {}) {
  return axios.delete(fullUrl(path), { ...baseConfig, ...opts });
}

/**
 * Multipart upload helper for FormData payloads (bug reports w/
 * screenshot+audio, image uploads, etc). Don't set Content-Type — the
 * browser must add the boundary itself.
 */
export async function apiUpload(path, formData, opts = {}) {
  return axios.post(fullUrl(path), formData, {
    ...baseConfig,
    ...opts,
  });
}

/**
 * Tiny error-message extractor: returns a human-readable string from
 * an axios response (handles both `{ detail: "..." }` FastAPI shape
 * and `{ message: "..." }` shape). Falls back to status-based text.
 */
export function apiError(res) {
  if (!res) return 'Errore di rete';
  const d = res.data || {};
  return d.detail || d.message || d.error || `Errore ${res.status || ''}`.trim();
}
